"use client";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";

import { Controller } from "react-hook-form";
import Checkbox from "@/components/atoms/FormComponents/Checkbox";
import Input from "@/components/atoms/FormComponents/Input/Input";
import Select from "@/components/atoms/FormComponents/Select";
import SubmitButton from "@/components/atoms/FormComponents/SubmitButton";
import cn from "clsx";
import styles from "./LandingFormMBA.module.scss";
import ApplicationFormRedirectLoader from "../../components/molecules/ApplicationFormRedirectLoader/ApplicationFormRedirectLoader";
import Markdown from "@/components/atoms/Markdown";
import { ALL_COUNTRIES } from "@/context/countries";
import ConsentFormPara from "@/components/common/utils/consentFormPara";
import ThankyouForInterestSection from "../../components/molecules/Forms/LandingPageConnectModalForm/Thankyou";
import CountryDropdownGlobal from "@/components/atoms/FormComponents/CustomDropdownGlobal/CustomDropdownGlobal";
import { useLandingConnectModalFormMBAController } from "./controller/useLandingConnectModalFormMBAController";
import type { LandingConnectModalFormProps } from "./model/landingConnectModalFormMBA.types";

const getCssConfig = () => ({
  firstName: cn(styles.inputWrapper),
  phoneNumber: cn(styles.commonMargins, styles.newLeadFormPhone),
  email: cn(styles.commonMargins, styles.emailContainer, "flex gap-4"),
  termsConditions: cn(styles.commonMargins, "flex items-start gap-[17px]"),
});

export default function LandingConnectModalFormMBA(
  props: LandingConnectModalFormProps,
) {
  const {
    control,
    errors,
    isSubmitting,
    isOTPLoading,
    isFormValid,
    disabledButton,
    isHeaderHidden,
    isApplicationRedirectLoaderDisabled,
    isThankYouBannerHidden,
    loadingCount,
    ctaText,
    btnColors,
    countryCodeOptions,
    message,
    isWebinar,
    isModal,
    setRefs,
    handleFieldChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormSubmit,
  } = useLandingConnectModalFormMBAController(props);

  const cssConfig = getCssConfig();

  return (
    <div
      id="#leadform"
      className={cn(
        styles.root,
        { [styles.greyBackground]: isWebinar },
        { [styles.whiteBackground]: isModal },
      )}
    >
      {!isApplicationRedirectLoaderDisabled ? (
        <div className={styles.applicationLoader}>
          <ApplicationFormRedirectLoader redirectTime={loadingCount} text="" />
        </div>
      ) : !isThankYouBannerHidden ? (
        <ThankyouForInterestSection />
      ) : (
        <InpSafeForm formRef={setRefs} onSubmit={handleFormSubmit}>
          {!isHeaderHidden && (
            <Markdown className={styles.formHeading}>
              {isWebinar ? ctaText : "Enquire Now"}
            </Markdown>
          )}
          <div
            className={cn(
              styles.formContainer,
              "h-full flex flex-col justify-between",
            )}
          >
            <div className={styles.formWrapper}>
              <div className={cssConfig.firstName}>
                <div className={cn(styles.widthInputboxFirstName, "w-[100%]")}>
                  <label htmlFor="full_name">
                    full name<span className="text-xl">*</span>
                  </label>
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Input
                        placeholder="Enter your full name"
                        value={field.value ?? ""}
                        onChangeFunc={(value: string) =>
                          handleFieldChange("fullName", value)
                        }
                        onBlur={() => {
                          field.onBlur();
                          handleFieldBlur(
                            "fullName",
                            "",
                            fieldState.error?.message || errors.fullName,
                          );
                        }}
                        id="full_name"
                      />
                    )}
                  />
                  <p className={cn(styles.error)}>{errors.fullName}</p>
                </div>
              </div>
              <div className={cssConfig.phoneNumber}>
                <div className="w-[100%]">
                  <label htmlFor="phone_num">
                    phone number <span className="text-xl">*</span>
                  </label>
                  <div className="flex gap-[6px]">
                    <div className={styles.phoneNumber_countryCode}>
                      <Controller
                        name="countryCode"
                        control={control}
                        render={({ field }) =>
                          isWebinar ? (
                            <CountryDropdownGlobal
                              onChangeFunc={(value: string) =>
                                handleFieldChange("countryCode", value)
                              }
                              countries={ALL_COUNTRIES}
                              countryCode={field.value}
                              updateCountryCode={updateCountryCode}
                              additionalStyle="h-[2.75rem] lg:h-[2.25rem]"
                              isDark={false}
                            />
                          ) : (
                            <Select
                              isFullWidth={false}
                              value={field.value ?? ""}
                              onChangeFunc={(value: string) =>
                                handleFieldChange("countryCode", value)
                              }
                              options={countryCodeOptions}
                              disabled={disabledButton}
                              className="!py-0"
                              id="countryCode"
                            />
                          )
                        }
                      />
                    </div>
                    <div className="w-full">
                      <Controller
                        name="phoneNumber"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Input
                            placeholder="Enter your no."
                            value={field.value ?? ""}
                            onChangeFunc={handlePhoneNumberChange}
                            onBlur={() => {
                              field.onBlur();
                              handleFieldBlur(
                                "phoneNumber",
                                "",
                                fieldState.error?.message || errors.phoneNumber,
                              );
                            }}
                            id="phone_num"
                            isTel
                          />
                        )}
                      />
                    </div>
                  </div>
                  <p className={cn(styles.error)}>
                    {errors.countryCode || errors.phoneNumber}
                  </p>
                </div>
              </div>

              <div className={cssConfig.email}>
                <div className="w-[100%]">
                  <label htmlFor="email_id">
                    email id <span className="text-xl">*</span>
                  </label>
                  <Controller
                    name="emailId"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Input
                        placeholder="abc@xyz.com"
                        value={field.value ?? ""}
                        onChangeFunc={(value: string) =>
                          handleFieldChange("emailId", value)
                        }
                        onBlur={() => {
                          field.onBlur();
                          handleFieldBlur(
                            "emailId",
                            "",
                            fieldState.error?.message || errors.emailId,
                          );
                        }}
                        id="email_id"
                      />
                    )}
                  />
                  <p className={cn(styles.error)}>{errors.emailId}</p>
                </div>
              </div>
            </div>

            <div className={cssConfig.termsConditions}>
              <div className="shrink-0">
                <Controller
                  name="isTermsAccepted"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      name="consent"
                      value={field.value}
                      onChangeFunc={(value: boolean) =>
                        handleFieldChange("isTermsAccepted", value)
                      }
                      onBlur={() => {
                        field.onBlur();
                        handleFieldBlur(
                          "isTermsAccepted",
                          "",
                          fieldState.error?.message ||
                            errors?.isTermsAccepted ||
                            "NA",
                        );
                      }}
                      id="agree_checkbox"
                    />
                  )}
                />
              </div>
              <p className={cn(styles.authorization)}>
                <ConsentFormPara />
              </p>
            </div>

            <SubmitButton
              text={isSubmitting || isOTPLoading ? "Submitting" : "Submit"}
              isDisabled={isSubmitting || isOTPLoading}
              bgColor={btnColors.bg}
              textColor={btnColors.color}
              id={"submit_button"}
            />
          </div>
          {message && message}
        </InpSafeForm>
      )}
    </div>
  );
}
